import { Button, FormLabel, HStack, Input, Select, Text, Wrap, WrapItem } from "@chakra-ui/react";

export interface PreFilterField {
  name: string;
  label: string;
  // CSS width for this field's input, e.g. "4ch" to fit a short code. Defaults to 160px.
  width?: string;
  // If set, renders a <Select> with these choices instead of a free-text <Input>.
  options?: { label: string; value: string }[];
}

interface PreFilterProps {
  fields: PreFilterField[];
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  onRun: () => void;
  running?: boolean;
  // Still resolving the field list itself (as opposed to `running`, which is a call in flight) -
  // shows loadingText and disables Run rather than rendering the (not yet known) fields.
  loading?: boolean;
  loadingText?: string;
  emptyText?: string;
  runLabel?: string;
}

// Reusable "restrict the call to the database" bar: a Run button plus a row of parameter inputs.
// Originally built for ReportRunner.tsx; kept generic (fields/values/onChange rather than
// anything report-specific) so other pages - e.g. DocLibrary - can reuse it against their own
// param list and fetch call.
function PreFilter({
  fields,
  values,
  onChange,
  onRun,
  running = false,
  loading = false,
  loadingText = "Loading parameters...",
  emptyText = "No parameters available.",
  runLabel = "Run",
}: PreFilterProps) {
  return (
    <HStack
      px={1}
      py={0.5}
      spacing={2}
      align="center"
      borderWidth={1}
      borderRadius="md"
      borderColor="pre-filter-border"
      bg="pre-filter-bg"
    >
      <Button
        variant="actionBar"
        onClick={onRun}
        isLoading={running}
        isDisabled={loading}
      >
        {runLabel}
      </Button>
      {loading ? (
        <Text color="gray.500" fontSize="sm">{loadingText}</Text>
      ) : fields.length === 0 ? (
        <Text color="gray.500" fontSize="sm">{emptyText}</Text>
      ) : (
        <Wrap spacing={1} align="center">
          {fields.map((f) => (
            <WrapItem key={f.name}>
              <HStack
                spacing={0.5}
                borderWidth="1px"
                borderColor="gray.300"
                borderRadius="md"
                bg="white"
                px={1}
                py={0}
              >
                <FormLabel fontSize="xs" mb={0} mr={0} whiteSpace="nowrap">{f.label}</FormLabel>
                {f.options ? (
                  <Select
                    size="xs"
                    h="20px"
                    minH="20px"
                    w={f.width ?? "100px"}
                    value={values[f.name] ?? ""}
                    onChange={(e) => onChange(f.name, e.target.value)}
                  >
                    {f.options.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    size="xs"
                    h="20px"
                    minH="20px"
                    py={0}
                    w={f.width ?? "160px"}
                    placeholder="ANY"
                    _placeholder={{ color: "gray.400" }}
                    value={values[f.name] ?? ""}
                    onChange={(e) => onChange(f.name, e.target.value)}
                  />
                )}
              </HStack>
            </WrapItem>
          ))}
        </Wrap>
      )}
    </HStack>
  );
}

export default PreFilter;
